import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import BottomNav from '@/components/BottomNav';

// Page wrapper: tropical background, room for the fixed desktop header and mobile bottom bar
export const Page = ({ children, nav = true, className = '', width = 'max-w-2xl', testId }) => (
  <div className={`min-h-screen tropic-bg ${nav ? 'pb-28 md:pb-12 md:pt-16' : ''} ${className}`} data-testid={testId}>
    {children}
    {nav && <BottomNav />}
  </div>
);

export const PageContent = ({ children, width = 'max-w-2xl', className = '' }) => (
  <main className={`container mx-auto ${width} px-4 sm:px-6 py-5 sm:py-8 ${className}`}>{children}</main>
);

// Sticky glass header with optional back button, title, subtitle and right-side actions
export const PageHeader = ({ title, subtitle, back = false, backTo, actions, children, width = 'max-w-2xl', icon: Icon }) => {
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 md:top-16 z-40 glass border-b">
      <div className={`container mx-auto ${width} px-4 sm:px-6 py-3`}>
        <div className="flex items-center gap-3">
          {back && (
            <button
              onClick={() => (backTo ? navigate(backTo) : navigate(-1))}
              className="h-10 w-10 shrink-0 rounded-full inline-flex items-center justify-center hover:bg-muted transition-colors"
              aria-label="Retour"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          {Icon && (
            <span className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center">
              <Icon className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0 flex-1">
            {typeof title === 'string' ? <h1 className="text-lg sm:text-xl font-bold truncate">{title}</h1> : title}
            {subtitle && <p className="text-xs text-muted-foreground truncate">{subtitle}</p>}
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </div>
        {children}
      </div>
    </header>
  );
};

export const EmptyState = ({ icon: Icon, emoji, title, description, action, testId }) => (
  <div className="surface flex flex-col items-center text-center px-6 py-14 border-dashed" data-testid={testId}>
    {Icon ? (
      <span className="h-16 w-16 rounded-2xl bg-primary/10 text-primary inline-flex items-center justify-center mb-4 animate-float">
        <Icon className="h-8 w-8" />
      </span>
    ) : (
      <div className="text-5xl mb-4 animate-float">{emoji || '🌴'}</div>
    )}
    <h3 className="text-lg font-bold mb-1">{title}</h3>
    {description && <p className="text-sm text-muted-foreground max-w-xs mb-6">{description}</p>}
    {action}
  </div>
);

export const SectionTitle = ({ children, eyebrow, action }) => (
  <div className="flex items-end justify-between mb-4">
    <div>
      {eyebrow && <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-primary mb-1">{eyebrow}</p>}
      <h2 className="text-base md:text-lg font-bold">{children}</h2>
    </div>
    {action}
  </div>
);
